import React, { useState, useEffect } from 'react';
import { adminService } from '../services/adminService';

function TemplateManager() {
  const [businessTypes, setBusinessTypes] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedBusiness, setSelectedBusiness] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [typesData, templatesData, businessesData] = await Promise.all([
        adminService.getBusinessTypes(),
        adminService.getTemplates(),
        adminService.getBusinessConfigs()
      ]);
      setBusinessTypes(typesData);
      setTemplates(templatesData);
      setBusinesses(businessesData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredBusinesses = businesses.filter(b =>
    b.business_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    b.business_id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSetTemplate = async () => {
    if (!selectedType || !selectedBusiness) {
      setError('Please select both business type and business config');
      return;
    }
    
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      await adminService.setTemplate(selectedType, selectedBusiness);
      setSuccess('Template set successfully');
      await loadData();
      setSelectedType('');
      setSelectedBusiness('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTemplate = async (businessType) => {
    if (!window.confirm(`Delete template for ${businessType}?`)) return;
    
    setLoading(true);
    try {
      await adminService.deleteTemplate(businessType);
      setSuccess('Template deleted');
      await loadData();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f9f9fb' }}>
      <nav className="bg-white shadow-sm" style={{ borderBottom: '1px solid #e5e7eb' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center space-x-4">
              <img src="/assets/logo---dark-with-color.png" alt="Remote Assistant" className="h-8 w-auto" />
              <h1 className="text-xl font-montserrat font-semibold" style={{ color: '#222324' }}>
                Template Manager
              </h1>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800">{error}</p>
          </div>
        )}
        
        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-800">{success}</p>
          </div>
        )}

        <div className="bg-white shadow-sm rounded-2xl p-6 mb-6" style={{ border: '1px solid #e5e7eb' }}>
          <h2 className="text-xl font-montserrat font-semibold mb-4" style={{ color: '#222324' }}>
            Set Default Template
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#222324' }}>
                Business Type
              </label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                style={{ borderColor: '#e5e7eb' }}
              >
                <option value="">Select type...</option>
                {businessTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#222324' }}>
                Search Business
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name or ID..."
                className="w-full px-3 py-2 border rounded-lg"
                style={{ borderColor: '#e5e7eb' }}
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-2" style={{ color: '#222324' }}>
              Business Configuration
            </label>
            <div className="border rounded-lg" style={{ borderColor: '#e5e7eb', maxHeight: '200px', overflowY: 'auto' }}>
              {filteredBusinesses.map(b => (
                <div
                  key={b.business_id}
                  onClick={() => setSelectedBusiness(b.business_id)}
                  className={`px-3 py-2 cursor-pointer hover:bg-gray-50 ${
                    selectedBusiness === b.business_id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                  }`}
                >
                  {b.business_name} ({b.business_id})
                </div>
              ))}
            </div>
          </div>

          <button
            onClick={handleSetTemplate}
            disabled={loading || !selectedType || !selectedBusiness}
            className="px-4 py-2 rounded-lg text-white font-medium"
            style={{ backgroundColor: '#055efd' }}
          >
            {loading ? 'Setting...' : 'Set Template'}
          </button>
        </div>

        <div className="bg-white shadow-sm rounded-2xl p-6" style={{ border: '1px solid #e5e7eb' }}>
          <h2 className="text-xl font-montserrat font-semibold mb-4" style={{ color: '#222324' }}>
            Current Templates
          </h2>
          
          <div className="space-y-3">
            {templates.map(t => (
              <div key={t.business_type} className="flex justify-between items-center p-4 border rounded-lg" style={{ borderColor: '#e5e7eb' }}>
                <div>
                  <p className="font-medium" style={{ color: '#222324' }}>{t.business_type}</p>
                  <p className="text-sm" style={{ color: '#979ba4' }}>{t.template_name}</p>
                </div>
                <button
                  onClick={() => handleDeleteTemplate(t.business_type)}
                  className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded"
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default TemplateManager;
